<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { UserRole } from '@sync-tracker/shared';
import { ROLE_LABEL } from '@sync-tracker/shared';
import { realtime } from '@/api/realtime';
import MentionsBell from '@/components/mentions/MentionsBell.vue';
import PinChatDialog from '@/components/ticket/PinChatDialog.vue';
import ResolveDialog from '@/components/ticket/ResolveDialog.vue';
import { formatRelative } from '@/lib/format';
import { useNow } from '@/lib/now';
import { useTabBadge } from '@/lib/tabBadge';
import { theme, toggleTheme } from '@/lib/theme';
import { useAuth } from '@/stores/auth';
import { useBoard } from '@/stores/board';
import { useMeta } from '@/stores/meta';
import { useNotifications } from '@/stores/notifications';
import { usePrefs } from '@/stores/prefs';
import { Icon, UiAvatar, UiMenu, UiSpinner } from '@/ui';

const auth = useAuth();
const meta = useMeta();
const board = useBoard();
const notifications = useNotifications();
const router = useRouter();
const now = useNow();

// свёрнутое меню — личная настройка (с любого браузера то же)
const prefsStore = usePrefs();
// ── Телефон: меню прячется в выдвижную панель по кнопке-бургеру ─────────────
const MOBILE_Q = '(max-width: 760px)';
const isMobile = ref(typeof window !== 'undefined' && window.matchMedia(MOBILE_Q).matches);
const menuOpen = ref(false);
const route = useRoute();
let mq: MediaQueryList | null = null;
const onMq = (e: MediaQueryListEvent) => {
  isMobile.value = e.matches;
  if (!e.matches) menuOpen.value = false;
};
const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (menuOpen.value = false);
onMounted(() => {
  mq = window.matchMedia(MOBILE_Q);
  mq.addEventListener('change', onMq);
  window.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  mq?.removeEventListener('change', onMq);
  window.removeEventListener('keydown', onKey);
});
// перешли в раздел — панель закрывается
watch(() => route.fullPath, () => (menuOpen.value = false));
// на телефоне подписи в панели видны всегда (свёрнутость — только для компьютера)
const collapsed = computed(() => !isMobile.value && prefsStore.prefs.ui.sidebarCollapsed);
function toggleSidebar() {
  prefsStore.prefs.ui.sidebarCollapsed = !prefsStore.prefs.ui.sidebarCollapsed;
}

interface NavItem {
  to: string;
  label: string;
  icon: string;
  role?: UserRole;
}
const NAV: { title?: string; items: NavItem[] }[] = [
  {
    items: [
      { to: '/board', label: 'Доска', icon: 'board' },
      { to: '/tickets', label: 'История', icon: 'list' },
      { to: '/analytics', label: 'Аналитика', icon: 'chart' },
    ],
  },
  {
    title: 'Работа',
    items: [
      { to: '/chats', label: 'Чаты', icon: 'chat' },
      { to: '/groups', label: 'Создать группу', icon: 'group', role: 'operator' },
      { to: '/invite', label: 'Добавить в чат', icon: 'user' },
      { to: '/broadcasts', label: 'Рассылки', icon: 'megaphone' },
      { to: '/my-columns', label: 'Мои колонки', icon: 'lock', role: 'operator' },
    ],
  },
  {
    title: 'Управление',
    items: [
      { to: '/directory', label: 'Справочники', icon: 'book' },
      { to: '/settings', label: 'Настройки', icon: 'settings', role: 'admin' },
      { to: '/users', label: 'Пользователи', icon: 'users', role: 'admin' },
      { to: '/audit', label: 'Аудит', icon: 'history', role: 'admin' },
    ],
  },
];
const nav = computed(() =>
  NAV.map((s) => ({ ...s, items: s.items.filter((i) => !i.role || auth.can(i.role)) })).filter((s) => s.items.length),
);

const openCount = computed(() => board.open.length);
useTabBadge(openCount);

// Индикатор здоровья: WebSocket + свежесть heartbeat движка
const engineState = computed(() => {
  void now.value;
  const hb = meta.data?.engine?.heartbeatAt;
  if (!meta.wsConnected) return { tone: 'danger', label: 'Нет связи с сервером' };
  if (!hb) return { tone: 'warning', label: 'Движок ещё не запускался' };
  const age = (now.value - new Date(hb).getTime()) / 1000;
  if (age > 90) return { tone: 'danger', label: `Движок молчит · ${formatRelative(hb, now.value)}` };
  return { tone: 'success', label: 'Онлайн' };
});

const userMenu = computed(() => [
  { key: 'header', label: auth.user?.login ?? '', header: true },
  { key: 'profile', label: 'Профиль и пароль', icon: 'user' },
  { key: 'theme', label: theme.value === 'dark' ? 'Светлая тема' : 'Тёмная тема', icon: theme.value === 'dark' ? 'sun' : 'moon' },
  { key: 'd', label: '', divider: true },
  { key: 'logout', label: 'Выйти', icon: 'logout', danger: true },
]);

async function onUserMenu(key: string) {
  if (key === 'profile') router.push('/profile');
  if (key === 'theme') toggleTheme();
  if (key === 'logout') {
    await auth.logout();
    router.push('/login');
  }
}

const ready = ref(false);
onMounted(async () => {
  realtime.connect();
  meta.wire();
  board.wire();
  notifications.wire();
  prefsStore.reset();
  await Promise.all([meta.load(), board.load(), prefsStore.load()]);
  ready.value = true;
});
</script>

<template>
  <div class="shell" :class="{ 'is-collapsed': collapsed, 'menu-open': menuOpen }">
    <Transition name="scrim">
      <div v-if="menuOpen" class="scrim" @click="menuOpen = false" />
    </Transition>
    <aside class="sidebar" :aria-hidden="isMobile && !menuOpen ? 'true' : undefined">
      <div class="brand">
        <div class="logo">
          <svg viewBox="0 0 32 32" width="28" height="28">
            <rect width="32" height="32" rx="9" fill="url(#g)" />
            <defs>
              <linearGradient id="g" x1="0" y1="0" x2="32" y2="32">
                <stop offset="0" stop-color="#7d8bff" />
                <stop offset="1" stop-color="#4f5ff0" />
              </linearGradient>
            </defs>
            <path
              d="M9 20.5c2.2 1.6 4.6 2.4 7 2.4 3.3 0 5.5-1.5 5.5-3.8 0-5-11.8-2.7-11.8-8.4C9.7 8.3 12 6.9 15 6.9c2.2 0 4.2.7 6 1.9"
              fill="none"
              stroke="#fff"
              stroke-width="2.6"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <div v-if="!collapsed" class="brand-text">
          <div class="brand-name">Sync Tracker</div>
          <div class="brand-sub">BS · клиентские запросы</div>
        </div>
        <button v-if="isMobile" class="drawer-close" type="button" aria-label="Закрыть меню" @click="menuOpen = false">
          <Icon name="close" :size="18" />
        </button>
      </div>

      <nav class="nav">
        <div v-for="(section, i) in nav" :key="i" class="nav-section">
          <div v-if="section.title && !collapsed" class="nav-title">{{ section.title }}</div>
          <RouterLink v-for="item in section.items" :key="item.to" :to="item.to" class="nav-item" :title="collapsed ? item.label : undefined">
            <Icon :name="item.icon" :size="17" />
            <span v-if="!collapsed" class="grow">{{ item.label }}</span>
            <span v-if="item.to === '/board' && openCount && !collapsed" class="nav-count">{{ openCount }}</span>
          </RouterLink>
        </div>
      </nav>

      <!-- телефон: пользователь, тема и выход прямо в панели -->
      <div v-if="isMobile" class="drawer-user">
        <UiAvatar :name="auth.user?.displayName" :size="34" />
        <div class="drawer-user-text">
          <span class="me-name">{{ auth.user?.displayName }}</span>
          <span class="me-role">{{ auth.user ? ROLE_LABEL[auth.user.role] : '' }}</span>
        </div>
        <button class="drawer-btn" type="button" :aria-label="theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'" @click="toggleTheme">
          <Icon :name="theme === 'dark' ? 'sun' : 'moon'" :size="17" />
        </button>
        <button class="drawer-btn" type="button" aria-label="Выйти" @click="onUserMenu('logout')">
          <Icon name="logout" :size="17" />
        </button>
      </div>
      <div v-else class="sidebar-foot">
        <button class="collapse" type="button" :title="collapsed ? 'Развернуть' : 'Свернуть'" @click="toggleSidebar">
          <Icon :name="collapsed ? 'chevronRight' : 'chevronLeft'" :size="15" />
        </button>
      </div>
    </aside>

    <div class="main">
      <header class="topbar">
        <button v-if="isMobile" class="burger" type="button" aria-label="Меню" :aria-expanded="menuOpen" @click="menuOpen = true">
          <Icon name="menu" :size="20" />
          <span v-if="openCount" class="burger-count">{{ openCount }}</span>
        </button>
        <MentionsBell />
        <div class="grow" />
        <div class="health" :class="`health--${engineState.tone}`" :title="engineState.label">
          <span class="pulse" />
          <span class="small">{{ engineState.label }}</span>
        </div>
        <UiMenu :items="userMenu" align="right" @select="onUserMenu">
          <button class="me" type="button">
            <UiAvatar :name="auth.user?.displayName" :size="28" />
            <span class="me-text">
              <span class="me-name">{{ auth.user?.displayName }}</span>
              <span class="me-role">{{ auth.user ? ROLE_LABEL[auth.user.role] : '' }}</span>
            </span>
            <Icon name="chevronDown" :size="14" class="faint" />
          </button>
        </UiMenu>
      </header>
      <main class="content">
        <slot v-if="ready" />
        <UiSpinner v-else label="Загружаю…" />
      </main>
      <ResolveDialog />
      <PinChatDialog />
    </div>
  </div>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: var(--sidebar-w) 1fr;
  height: 100vh;
  transition: grid-template-columns 200ms var(--ease);
}
.shell.is-collapsed {
  grid-template-columns: 64px 1fr;
}
.sidebar {
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border);
  background: color-mix(in srgb, var(--surface) 70%, transparent);
  backdrop-filter: blur(8px);
  min-width: 0;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  height: var(--topbar-h);
  padding: 0 16px;
  border-bottom: 1px solid var(--divider);
}
.logo {
  display: grid;
  place-items: center;
  filter: drop-shadow(0 4px 12px rgba(91, 108, 255, 0.35));
}
.brand-name {
  font-weight: 650;
  letter-spacing: -0.01em;
}
.brand-sub {
  font-size: 11px;
  color: var(--faint);
}
.nav {
  flex: 1;
  overflow-y: auto;
  padding: 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.nav-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--faint);
  padding: 4px 10px 6px;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 11px;
  height: 36px;
  padding: 0 10px;
  border-radius: 9px;
  color: var(--text-2);
  font-weight: 500;
  text-decoration: none;
  transition:
    background var(--dur) var(--ease),
    color var(--dur) var(--ease);
}
.is-collapsed .nav-item {
  justify-content: center;
  padding: 0;
}
.nav-item:hover {
  background: var(--surface-hover);
  color: var(--text);
  text-decoration: none;
}
.nav-item.router-link-active {
  background: var(--accent-soft);
  color: var(--accent-text);
}
.nav-count {
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: var(--accent);
  color: #fff;
}
.sidebar-foot {
  padding: 10px;
  border-top: 1px solid var(--divider);
  display: flex;
  justify-content: flex-end;
}
.is-collapsed .sidebar-foot {
  justify-content: center;
}
.collapse {
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--muted);
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  cursor: pointer;
}
.collapse:hover {
  color: var(--text);
}
.main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
.topbar {
  height: var(--topbar-h);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 20px;
  border-bottom: 1px solid var(--border);
  background: color-mix(in srgb, var(--bg) 70%, transparent);
  backdrop-filter: blur(8px);
}
.content {
  flex: 1;
  overflow: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.health {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--text-2);
}
.pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.health--success .pulse {
  background: var(--success);
  box-shadow: 0 0 0 3px var(--success-soft);
}
.health--warning .pulse {
  background: var(--warning);
  box-shadow: 0 0 0 3px var(--warning-soft);
}
.health--danger {
  color: var(--danger);
}
.health--danger .pulse {
  background: var(--danger);
  animation: st-pulse 1.2s infinite;
}
.me {
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  padding: 4px 6px 4px 4px;
  border-radius: 10px;
  cursor: pointer;
}
.me:hover {
  background: var(--surface-hover);
}
.me-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}
.me-name {
  font-weight: 500;
}
.me-role {
  font-size: 11px;
  color: var(--faint);
}
/* ── Телефон: колонки меню нет, меню — выдвижная панель слева по бургеру ── */
.burger,
.drawer-close,
.scrim {
  display: none;
}
@media (max-width: 760px) {
  .shell,
  .shell.is-collapsed {
    grid-template-columns: 1fr;
  }
  .sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 120;
    width: min(300px, 84vw);
    background: var(--surface);
    backdrop-filter: none;
    box-shadow: var(--shadow-lg);
    transform: translateX(-102%);
    transition: transform 240ms var(--ease);
  }
  .menu-open .sidebar {
    transform: none;
  }
  .scrim {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 110;
    background: var(--overlay);
    backdrop-filter: blur(2px);
  }
  .brand {
    position: relative;
  }
  .drawer-close {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    margin-left: auto;
    border: none;
    border-radius: 9px;
    background: transparent;
    color: var(--muted);
    cursor: pointer;
  }
  .drawer-close:hover {
    background: var(--surface-hover);
    color: var(--text);
  }
  .nav-item {
    min-height: 44px;
    font-size: 15px;
  }
  .burger {
    position: relative;
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: var(--surface-2);
    color: var(--text-2);
    cursor: pointer;
  }
  .burger:active {
    transform: scale(0.96);
  }
  .burger-count {
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: var(--accent);
    color: #fff;
    font-size: 10.5px;
    font-weight: 700;
    box-shadow: 0 0 0 2px var(--bg);
  }
  .me-text,
  .health .small {
    display: none;
  }
}
.drawer-user {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding: 12px 14px calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--divider);
}
.drawer-user-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.drawer-btn {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
}
.drawer-btn:hover {
  background: var(--surface-hover);
  color: var(--text);
}
.scrim-enter-active,
.scrim-leave-active {
  transition: opacity 200ms ease;
}
.scrim-enter-from,
.scrim-leave-to {
  opacity: 0;
}
</style>
